
import { isValidObjectId } from "@/lib/utils/input-validator";
import { extractAssetKey } from "@/lib/utils/asset-key";
import { handleErrorServer, ServerError } from "@/lib/api/errors/ErrorHandler";
import { logApiFailure } from "@/lib/api/errors/redactError";
import {
  deleteImages,
  getUrls,
  uploadImages,
  uploadFavicon,
  getFavicon32Url,
  uploadTitleImage,
  copyImageToTitleImage,
  getEventImageUrl,
  getEventImageUrls,
  uploadOgImage,
  getOgImageUrls,
  getPresignedVideoUploadUrl,
  getVideoUrls,
  deleteVideos,
} from "@/lib/api/clients/S3";

export async function getS3PictureUrls(
  eventId: string,
): Promise<string[] | ServerError> {
  try {
    if (!isValidObjectId(eventId)) throw new Error("validationError");


    return await getUrls(eventId);
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function uploadS3Images(
  eventId: string,
  files: File[],
): Promise<string[] | ServerError> {
  try {
    if (!isValidObjectId(eventId)) throw new Error("validationError");

    if (files.length === 0) throw new Error("validationError");


    return await uploadImages(eventId, files);
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function uploadS3TitleImage(
  eventId: string,
  file: File,
): Promise<void | ServerError> {
  try {
    if (!isValidObjectId(eventId)) throw new Error("validationError");

    if (!file) throw new Error("validationError");


    return await uploadTitleImage(eventId, file);
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function deleteS3Images(
  eventId: string,
  imageNames: string[],
): Promise<void | ServerError> {
  try {
    if (!isValidObjectId(eventId)) throw new Error("validationError");

    if (!imageNames || imageNames.length === 0)
      throw new Error("validationError");


    return await deleteImages(eventId, imageNames);
  } catch (error) {
    return handleErrorServer(error);
  }
}

/**
 * Uploads a favicon image and generates all required sizes.
 * The original image must be a 512x512 PNG.
 *
 * @param eventId The event ID
 * @param faviconFile The 512x512 PNG file
 * @returns The URL of the 32x32 favicon or a ServerError
 */
export async function uploadS3Favicon(
  eventId: string,
  faviconFile: File,
): Promise<string | ServerError> {
  try {
    // Validate event ID
    if (!isValidObjectId(eventId)) throw new Error("validationError");

    // Validate file
    if (!faviconFile) throw new Error("validationError");

    // Check if file is PNG
    if (!faviconFile.type.includes("png")) throw new Error("faviconMustBePng");


    return await uploadFavicon(eventId, faviconFile);
  } catch (error) {
    return handleErrorServer(error);
  }
}

/**
 * Gets the URL for the 32x32 favicon
 *
 * @param eventId The event ID
 * @returns The favicon URL or a ServerError
 */
export async function getS3Favicon(
  eventId: string,
): Promise<string | ServerError> {
  try {
    if (!isValidObjectId(eventId)) throw new Error("validationError");

    const url = await getFavicon32Url(eventId);

    // If URL is empty, consider it an error
    if (!url) throw new Error("faviconNotFound");

    return url;
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function copyS3ImageToTitleImage(
  eventId: string,
  imageName: string,
): Promise<string | ServerError> {
  try {
    if (!isValidObjectId(eventId)) throw new Error("validationError");

    // Pickers store the FULL asset URL since M20 (2026-07-05 W2-E); the
    // backend endpoint validates a raw S3 key — normalize here so both the
    // new URL shape and legacy bare keys keep working.
    return await copyImageToTitleImage(
      eventId,
      imageName ? extractAssetKey(imageName) : imageName,
    );
  } catch (error) {
    return handleErrorServer(error);
  }
}

/**
 * Gets the presigned URL for the event's title image
 *
 * @param eventId The event ID
 * @returns The event image URL or a ServerError
 */
export async function getS3EventImageUrl(
  eventId: string,
): Promise<string | ServerError> {
  try {
    if (!isValidObjectId(eventId)) throw new Error("validationError");

    const url = await getEventImageUrl(eventId);

    return url || "";
  } catch (error) {
    return handleErrorServer(error);
  }
}

/**
 * Gets the presigned URLs for multiple events' title images
 *
 * @param eventIds Array of event IDs
 * @returns Map of event IDs to their presigned URLs or a ServerError
 */
export async function getS3EventImageUrls(
  eventIds: string[],
): Promise<Record<string, string> | ServerError> {
  try {
    // Validate input
    if (!eventIds || eventIds.length === 0) throw new Error("validationError");

    // Validate all event IDs
    for (const eventId of eventIds) {
      if (!isValidObjectId(eventId)) throw new Error("validationError");
    }

    const urlMap = await getEventImageUrls(eventIds);

    return urlMap || {};
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function uploadS3OgImage(
  eventId: string,
  file: File,
): Promise<string | ServerError> {
  try {
    if (!isValidObjectId(eventId)) throw new Error("validationError");
    if (!file) throw new Error("validationError");

    return await uploadOgImage(eventId, file);
  } catch (error) {
    return logApiFailure("s3.uploadOgImage failed", error);
  }
}

export async function getS3OgImageUrls(
  eventId: string,
): Promise<string[] | ServerError> {
  try {
    if (!isValidObjectId(eventId)) throw new Error("validationError");

    return await getOgImageUrls(eventId);
  } catch (error) {
    return logApiFailure("s3.getOgImageUrls failed", error);
  }
}

export async function getS3PresignedVideoUploadUrl(
  eventId: string,
  fileName: string,
  contentType: string,
  fileSize: number,
): Promise<{ uploadUrl: string; publicUrl: string } | ServerError> {
  try {
    if (!isValidObjectId(eventId)) throw new Error("validationError");

    return await getPresignedVideoUploadUrl(
      eventId,
      fileName,
      contentType,
      fileSize,
    );
  } catch (error) {
    return logApiFailure("s3.getPresignedVideoUploadUrl failed", error);
  }
}

export async function getS3VideoUrls(
  eventId: string,
): Promise<string[] | ServerError> {
  try {
    if (!isValidObjectId(eventId)) throw new Error("validationError");

    return await getVideoUrls(eventId);
  } catch (error) {
    return logApiFailure("s3.getVideoUrls failed", error);
  }
}

export async function deleteS3Videos(
  eventId: string,
  videoNames: string[],
): Promise<void | ServerError> {
  try {
    if (!isValidObjectId(eventId)) throw new Error("validationError");

    if (!videoNames || videoNames.length === 0)
      throw new Error("validationError");

    return await deleteVideos(eventId, videoNames);
  } catch (error) {
    return handleErrorServer(error);
  }
}
